<!--
  MainChart.vue
  メインチャートコンポーネント
  
  Created: 2025-08-11
  Author: Claude
-->
<template>
  <div class="main-chart-container">
    <!-- ローディング表示 -->
    <div v-if="isLoading" class="chart-loading">
      <div class="loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
      <p>データを読み込んでいます...</p>
    </div>
    
    <!-- チャート表示 -->
    <div v-else class="chart-wrapper">
      <canvas ref="chartCanvas"></canvas>
    </div>
    
    <!-- データなし表示 -->
    <div v-if="!isLoading && (!data || !data.datasets || data.datasets.length === 0)" class="no-data">
      <i class="fas fa-chart-area"></i>
      <p>表示するデータがありません</p>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';

export default {
  name: 'MainChart',
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => {
        return ['applications', 'interviews', 'conversion', 'sources', 'stages'].includes(value);
      }
    },
    data: {
      type: Object,
      default: () => ({})
    },
    period: {
      type: String,
      default: 'daily'
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      chart: null,
      chartOptions: {
        applications: {
          type: 'line',
          title: '応募数推移',
          yAxisLabel: '応募数',
          colors: ['#3498db', '#2ecc71', '#e74c3c']
        },
        interviews: {
          type: 'bar',
          title: '面談実施状況',
          yAxisLabel: '面談数',
          colors: ['#9b59b6', '#f39c12', '#1abc9c']
        },
        conversion: {
          type: 'line',
          title: '採用転換率',
          yAxisLabel: '転換率 (%)',
          colors: ['#e67e22', '#34495e', '#16a085']
        },
        sources: {
          type: 'doughnut',
          title: '媒体別応募比率',
          colors: ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c']
        },
        stages: {
          type: 'bar',
          title: 'ステージ別人数',
          yAxisLabel: '人数',
          colors: ['#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6']
        }
      }
    };
  },
  
  mounted() {
    this.initChart();
  },
  
  watch: {
    type() {
      this.updateChart();
    },
    
    data: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    
    period() {
      this.updateChart();
    },
    
    isLoading(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.updateChart();
        });
      }
    }
  },
  
  methods: {
    /**
     * チャートの初期化
     */
    initChart() {
      if (this.isLoading || !this.data || !this.$refs.chartCanvas) return;
      
      const ctx = this.$refs.chartCanvas.getContext('2d');
      const options = this.chartOptions[this.type];
      
      // チャート設定
      const config = {
        type: options.type,
        data: this.processChartData(),
        options: this.getChartOptions(options)
      };
      
      this.chart = new Chart(ctx, config);
    },
    
    /**
     * チャートの更新
     */
    updateChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      
      if (!this.isLoading && this.data && this.$refs.chartCanvas) {
        this.initChart();
      }
    },
    
    /**
     * チャートデータの処理
     */
    processChartData() {
      const options = this.chartOptions[this.type];
      
      // データがない場合のフォールバック
      if (!this.data || !this.data.datasets) {
        return this.generateSampleData(options);
      }
      
      // データセットの色設定
      const processedData = {
        labels: this.data.labels || [],
        datasets: this.data.datasets.map((dataset, index) => ({
          ...dataset,
          backgroundColor: dataset.backgroundColor || 
            (options.type === 'line' ? 
              options.colors[index % options.colors.length] + '20' : 
              options.colors[index % options.colors.length]),
          borderColor: dataset.borderColor || options.colors[index % options.colors.length],
          borderWidth: dataset.borderWidth || 2,
          tension: dataset.tension !== undefined ? dataset.tension : 0.4,
          fill: dataset.fill !== undefined ? dataset.fill : (options.type === 'line')
        }))
      };
      
      return processedData;
    },
    
    /**
     * チャートオプションの取得
     */
    getChartOptions(options) {
      const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              padding: 15,
              font: {
                size: 12,
                family: "'BIZ UDPGothic', sans-serif"
              },
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleFont: {
              size: 12,
              family: "'BIZ UDPGothic', sans-serif"
            },
            bodyFont: {
              size: 11,
              family: "'BIZ UDPGothic', sans-serif"
            },
            padding: 10,
            displayColors: true,
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                
                if (options.yAxisLabel && options.yAxisLabel.includes('%')) {
                  label += context.parsed.y.toFixed(1) + '%';
                } else {
                  label += context.parsed.y.toLocaleString();
                }
                
                return label;
              }
            }
          }
        }
      };
      
      // 円グラフ・ドーナツグラフの場合
      if (options.type === 'doughnut' || options.type === 'pie') {
        return {
          ...baseOptions,
          plugins: {
            ...baseOptions.plugins,
            tooltip: {
              ...baseOptions.plugins.tooltip,
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                }
              }
            }
          }
        };
      }
      
      // 線グラフ・棒グラフの場合
      return {
        ...baseOptions,
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              font: {
                size: 11,
                family: "'BIZ UDPGothic', sans-serif"
              },
              color: '#666',
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: this.getMaxTicksLimit()
            }
          },
          y: {
            display: true,
            position: 'left',
            grid: {
              color: '#f0f0f0',
              drawBorder: false
            },
            ticks: {
              font: {
                size: 11,
                family: "'BIZ UDPGothic', sans-serif"
              },
              color: '#666',
              callback: (value) => {
                if (options.yAxisLabel && options.yAxisLabel.includes('%')) {
                  return value + '%';
                }
                return value.toLocaleString();
              }
            },
            title: {
              display: true,
              text: options.yAxisLabel || '',
              font: {
                size: 12,
                family: "'BIZ UDPGothic', sans-serif"
              },
              color: '#495057'
            }
          }
        }
      };
    },
    
    /**
     * X軸の最大ティック数を取得
     */
    getMaxTicksLimit() {
      switch(this.period) {
        case 'daily':
          return 10;
        case 'weekly':
          return 8;
        case 'monthly':
          return 12;
        case 'yearly':
          return 5;
        default:
          return 10;
      }
    },
    
    /**
     * サンプルデータの生成
     */
    generateSampleData(options) {
      const labels = this.generateLabels();
      const datasets = [];
      
      if (options.type === 'doughnut' || options.type === 'pie') {
        // 円グラフ用データ
        datasets.push({
          data: [35, 25, 20, 15, 5],
          backgroundColor: options.colors.slice(0, 5)
        });
        
        return {
          labels: ['Indeed', 'マイナビ', 'リクナビ', 'エン転職', 'その他'],
          datasets
        };
      } else {
        // 線グラフ・棒グラフ用データ
        const datasetCount = options.type === 'line' ? 2 : 1;
        
        for (let i = 0; i < datasetCount; i++) {
          datasets.push({
            label: `データセット${i + 1}`,
            data: this.generateRandomData(labels.length),
            backgroundColor: options.type === 'bar' ? 
              options.colors[i] : 
              options.colors[i] + '20',
            borderColor: options.colors[i],
            borderWidth: 2,
            tension: 0.4,
            fill: options.type === 'line'
          });
        }
        
        return { labels, datasets };
      }
    },
    
    /**
     * ラベルの生成
     */
    generateLabels() {
      const count = this.getLabelCount();
      
      switch(this.period) {
        case 'daily':
          return Array.from({length: count}, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (count - 1 - i));
            return `${date.getMonth() + 1}/${date.getDate()}`;
          });
          
        case 'weekly':
          return Array.from({length: count}, (_, i) => `第${i + 1}週`);
          
        case 'monthly':
          return Array.from({length: count}, (_, i) => {
            const date = new Date();
            date.setMonth(date.getMonth() - (count - 1 - i));
            return `${date.getFullYear()}年${date.getMonth() + 1}月`;
          });
          
        case 'yearly':
          return Array.from({length: count}, (_, i) => {
            const year = new Date().getFullYear() - (count - 1 - i);
            return `${year}年`;
          });
          
        default:
          return Array.from({length: count}, (_, i) => `Point ${i + 1}`);
      }
    },
    
    /**
     * ラベル数の取得
     */
    getLabelCount() {
      switch(this.period) {
        case 'daily':
          return 30;
        case 'weekly':
          return 12;
        case 'monthly':
          return 12;
        case 'yearly':
          return 5;
        default:
          return 10;
      }
    },
    
    /**
     * ランダムデータの生成
     */
    generateRandomData(length) {
      return Array.from({length}, () => Math.floor(Math.random() * 100) + 10);
    }
  },
  
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
</script>

<style lang="scss" scoped>
.main-chart-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.chart-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  
  canvas {
    max-height: 400px;
  }
}

.chart-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
  
  .loading-spinner {
    font-size: 32px;
    color: #3498db;
    margin-bottom: 16px;
    
    i {
      animation: spin 1s linear infinite;
    }
  }
  
  p {
    font-size: 14px;
    color: #666;
    margin: 0;
  }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
  color: #999;
  
  i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// レスポンシブ対応
@media (max-width: 768px) {
  .main-chart-container {
    min-height: 300px;
  }
  
  .chart-wrapper canvas {
    max-height: 300px;
  }
  
  .chart-loading,
  .no-data {
    min-height: 300px;
  }
}

@media (max-width: 576px) {
  .main-chart-container {
    min-height: 250px;
  }
  
  .chart-wrapper canvas {
    max-height: 250px;
  }
  
  .chart-loading,
  .no-data {
    min-height: 250px;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/DashBoard/Report/components/MainChart.vue